import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './suggest-content.module.scss';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import analyticsService from 'src/helpers/analytics.service';
import { EVENTS } from 'shared/constants';
import { FileUploadV2 } from 'src/components/core/fileupload/FileUploadV2.component';
import { MIME_TYPES_DISPLAY_NAMES } from 'shared/constants/assets.constants';
import { Button, ScreenTitle } from 'src/components/design-system';
import { TextInput, TextAreaInput, RequiredIndication } from 'src/components/design-system/forms';
import {
  SUGGEST_CONTENT_FILE_FORMATS,
  SUGGEST_CONTENT_IMAGE_FORMATS,
} from 'shared/constants/suggest-content.constants';
import classNames from 'classnames';

const OPTIONAL_LABEL = '(optional)';

const FIELD_NAMES = {
  TITLE: 'title',
  URL: 'url',
  COMMENT: 'comment',
  FILE_NAME: 'fileName',
  FILE_LINK: 'fileLink',
  IMAGE_NAME: 'imageName',
  IMAGE_LINK: 'imageLink',
};

const SUGGESTION_SCHEMA = Yup.object().shape({
  [FIELD_NAMES.TITLE]: Yup.string().max(200, 'Too Long!').required('Required!'),
  [FIELD_NAMES.URL]: Yup.string(),
  [FIELD_NAMES.COMMENT]: Yup.string().max(2000, 'Too Long!'),
  [FIELD_NAMES.FILE_NAME]: Yup.string(),
  [FIELD_NAMES.FILE_LINK]: Yup.string(),
  [FIELD_NAMES.IMAGE_NAME]: Yup.string(),
  [FIELD_NAMES.IMAGE_LINK]: Yup.string(),
});

const SuggestContentComponent = ({ lang, suggestContent, uploadFile, api }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const goBack = useCallback(() => navigate(-1), [navigate]);
  const openNotification = useCallback(
    error => {
      if (error) {
        api.error({
          title: lang.NOTIFICATION_ERROR,
          placement: 'bottomRight',
        });
        return;
      }
      api.success({
        title: lang.NOTIFICATION_SUCCESS,
        placement: 'bottomRight',
      });
    },

    [api, lang],
  );

  const submitSuggestion = useCallback(
    async data => {
      setLoading(true);
      const res = await suggestContent({ data });
      analyticsService.track(EVENTS.SUGGEST_CONTENT.SUBMIT, {
        title: data?.title,
        link: data?.url,
        fileUrl: data?.fileLink,
        imageUrl: data?.imageLink,
        comment: data?.comment,
        assignmentId: res?.data?.assignment?.id,
      });
      setLoading(false);
      openNotification(res?.error);
      goBack();
    },
    [suggestContent, openNotification, goBack],
  );

  const { handleSubmit, handleChange, setFieldValue, values, errors, isValid } = useFormik({
    validationSchema: SUGGESTION_SCHEMA,
    validateOnMount: true,
    initialValues: {
      [FIELD_NAMES.TITLE]: '',
      [FIELD_NAMES.URL]: '',
      [FIELD_NAMES.COMMENT]: '',
      [FIELD_NAMES.FILE_NAME]: '',
      [FIELD_NAMES.FILE_LINK]: '',
      [FIELD_NAMES.IMAGE_NAME]: '',
      [FIELD_NAMES.IMAGE_LINK]: '',
    },
    onSubmit: submitSuggestion,
  });

  const createUploadProcess = useCallback(
    (fieldName, linkFieldName) =>
      async ({ file, onSuccess, onError, onProgress }) => {
        try {
          const res = await uploadFile({
            file,
            onUploadProgress: e => onProgress({ percent: (e.loaded / e.total) * 100 }),
          });

          if (res?.error) {
            openNotification(res?.error);
            onError(new Error(res.error));
            return;
          }

          setFieldValue(fieldName, file.name);
          setFieldValue(linkFieldName, res?.url);
          onSuccess({ url: res?.url });
        } catch (err) {
          openNotification(err?.message);
          onError(err);
        }
      },
    [openNotification, setFieldValue, uploadFile],
  );

  const handleFileUpload = useMemo(
    () => createUploadProcess(FIELD_NAMES.FILE_NAME, FIELD_NAMES.FILE_LINK),
    [createUploadProcess],
  );

  const handleImageUpload = useMemo(
    () => createUploadProcess(FIELD_NAMES.IMAGE_NAME, FIELD_NAMES.IMAGE_LINK),
    [createUploadProcess],
  );

  const handleFileRemove = useCallback(() => {
    setFieldValue(FIELD_NAMES.FILE_NAME, '');
    setFieldValue(FIELD_NAMES.FILE_LINK, '');
  }, [setFieldValue]);

  const handleImageRemove = useCallback(() => {
    setFieldValue(FIELD_NAMES.IMAGE_NAME, '');
    setFieldValue(FIELD_NAMES.IMAGE_LINK, '');
  }, [setFieldValue]);

  return (
    <div className={classNames(styles.root, 'new-design-system')}>
      <ScreenTitle title={lang.TITLE} />
      <form onSubmit={handleSubmit} className={styles.form}>
        <TextInput
          label={<RequiredIndication label={lang.FORM_TITLE} />}
          name={FIELD_NAMES.TITLE}
          value={values[FIELD_NAMES.TITLE]}
          error={errors[FIELD_NAMES.TITLE]}
          onChange={handleChange}
          allowClear
          required
        />

        <TextInput
          name={FIELD_NAMES.URL}
          value={values[FIELD_NAMES.URL]}
          error={errors[FIELD_NAMES.URL]}
          onChange={handleChange}
          allowClear
          label={`${lang.FORM_URL} ${OPTIONAL_LABEL}`}
        />

        <div className={styles.inputContainer}>
          <div className={styles.labelContainer}>
            <label
              className={styles.inputLabel}
            >{`${lang.FORM_UPLOAD_FILE} ${OPTIONAL_LABEL}`}</label>
          </div>
          <FileUploadV2
            accept={SUGGEST_CONTENT_FILE_FORMATS.join(',')}
            maxCount={1}
            customRequest={handleFileUpload}
            onRemove={handleFileRemove}
            hint={SUGGEST_CONTENT_FILE_FORMATS.map(
              mimeType => MIME_TYPES_DISPLAY_NAMES[mimeType] || mimeType,
            ).join(', ')}
          />
        </div>

        <div className={styles.inputContainer}>
          <div className={styles.labelContainer}>
            <label
              className={styles.inputLabel}
            >{`${lang.FORM_UPLOAD_IMAGE} ${OPTIONAL_LABEL}`}</label>
          </div>
          <FileUploadV2
            accept={SUGGEST_CONTENT_IMAGE_FORMATS.join(',')}
            maxCount={1}
            customRequest={handleImageUpload}
            onRemove={handleImageRemove}
            hint={SUGGEST_CONTENT_IMAGE_FORMATS.map(
              mimeType => MIME_TYPES_DISPLAY_NAMES[mimeType] || mimeType,
            ).join(', ')}
          />
        </div>

        <TextAreaInput
          name={FIELD_NAMES.COMMENT}
          value={values[FIELD_NAMES.COMMENT]}
          error={errors[FIELD_NAMES.COMMENT]}
          onChange={handleChange}
          label={lang.FORM_COMMENTS}
          rows={11}
          className={styles.textArea}
        />

        <Button
          loading={loading}
          disabled={!isValid}
          htmlType="submit"
          className={styles.submitButton}
        >
          {lang.FORM_SUBMIT}
        </Button>
      </form>
    </div>
  );
};

export { SuggestContentComponent };
